import { useEffect, useRef } from 'react';

/**
 * Hook pour enregistrer des raccourcis clavier globaux.
 *
 * @param {Object} shortcuts - Map des raccourcis : { 'ctrl+n': handler, 'escape': handler, ... }
 * @param {Object} options
 * @param {boolean} options.enabled - Activer/désactiver les raccourcis (default: true)
 * @param {boolean} options.ignoreInputs - Ignorer les raccourcis quand un input est focus (default: true)
 *
 * Modificateurs supportés : ctrl, cmd (meta), alt, shift
 * Exemples de clés : 'ctrl+n', 'cmd+k', 'escape', 'shift+?', 'ctrl+1'..'ctrl+9'
 */
export function useKeyboardShortcuts(shortcuts = {}, options = {}) {
  const { enabled = true, ignoreInputs = true } = options;
  const shortcutsRef = useRef(shortcuts);

  // Garder la référence à jour sans ré-enregistrer l'event listener
  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  useEffect(() => {
    if (!enabled) return;

    const isInputFocused = () => {
      if (!ignoreInputs) return false;
      const active = document.activeElement;
      if (!active) return false;
      const tag = active.tagName.toLowerCase();
      const isContentEditable = active.isContentEditable;
      const isInput = tag === 'input' || tag === 'textarea' || tag === 'select';
      return isInput || isContentEditable;
    };

    const normalizeKey = (e) => {
      const parts = [];
      if (e.ctrlKey) parts.push('ctrl');
      if (e.metaKey) parts.push('cmd');
      if (e.altKey) parts.push('alt');
      if (e.shiftKey) parts.push('shift');

      let key = e.key.toLowerCase();

      // Alias
      if (key === 'escape') key = 'esc';
      if (key === ' ') key = 'space';
      if (key === '?') key = '?';

      // Pour les chiffres et lettres, on les ajoute sans modificateur si ce n'est que shift
      if (key.length === 1) {
        parts.push(key);
      } else {
        // Touches spéciales (enter, tab, arrowleft, etc.)
        if (!parts.includes(key)) {
          parts.push(key);
        }
      }

      return parts.join('+');
    };

    const handler = (e) => {
      const combo = normalizeKey(e);
      const currentShortcuts = shortcutsRef.current;

      // Vérifier les combinaisons exactes
      for (const [shortcut, callback] of Object.entries(currentShortcuts)) {
        const normalizedShortcut = shortcut.toLowerCase().replace(/\s+/g, '');
        if (combo === normalizedShortcut) {
          // Ignorer les inputs sauf pour Escape et quelques raccourcis spéciaux
          if (isInputFocused() && !['esc', 'escape'].includes(normalizedShortcut)) {
            continue;
          }
          e.preventDefault();
          callback(e);
          return;
        }
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [enabled, ignoreInputs]);
}

export default useKeyboardShortcuts;
